import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { ACCENT, DOURADO } from "../campanhas/CampanhaVisuals";
import { origemDaVisita } from "./capturaLead";
import {
  chamarSimulador,
  partesComNegrito,
  useTurnstile,
} from "./simuladorCliente";

// Página pública do simulador com IA (sem autenticação) — porta de entrada do
// cliente final. A conversa e todos os limites (Turnstile, perguntas por
// sessão, sessões por IP, teto do dia) estão na edge function simulador-site;
// aqui é só a tela. Nome e WhatsApp vêm antes da conversa (Rick, 18/10/2026):
// o "iniciar" já grava o lead, e cada pergunta atualiza o card. A saudação é
// fixa para não gastar IA com "olá".

const AZUL_ESCURO = "#263751";
const LIMITE_CARACTERES = 400;
const CHAVE_SESSAO = "north-simulador";

type Mensagem = { papel: "cliente" | "ia"; texto: string };

const saudacao = (nome: string | null): Mensagem => {
  const primeiro = (nome ?? "").trim().split(/\s+/)[0];
  return {
    papel: "ia",
    texto: `${primeiro ? `Olá, ${primeiro}.` : "Olá."} Sou a assistente virtual da North e vou simular um consórcio com você. O que você quer conquistar: um imóvel ou um veículo?`,
  };
};

const SUGESTOES = ["Imóvel", "Veículo", "Como funciona o consórcio?"];

type Estado = {
  sessao: string | null;
  mensagens: Mensagem[];
  contato: boolean;
  encerrada: boolean;
};

const estadoVazio = (): Estado => ({
  sessao: null,
  mensagens: [],
  contato: false,
  encerrada: false,
});

const estadoInicial = (): Estado => {
  try {
    const salvo = JSON.parse(sessionStorage.getItem(CHAVE_SESSAO) ?? "null");
    if (salvo?.sessao && Array.isArray(salvo.mensagens)) return salvo;
  } catch {
    // sessionStorage indisponível (aba anônima com bloqueio): começa do zero.
  }
  return estadoVazio();
};

const Texto = ({ texto }: { texto: string }) => (
  <>
    {partesComNegrito(texto).map((p, i) =>
      p.negrito ? (
        <strong key={i}>{p.texto}</strong>
      ) : (
        <span key={i}>{p.texto}</span>
      ),
    )}
  </>
);

const AVISO_LEGAL =
  "Valores estimados, sujeitos à disponibilidade de grupos e às condições da administradora. A contemplação ocorre por sorteio ou lance, sem data garantida.";

type Campo = "nome" | "whatsapp" | "email";

/** Nome, WhatsApp e e-mail opcional: o envio abre a sessão e grava o lead. */
const FormularioInicio = ({
  origem,
  aoIniciar,
}: {
  origem: Record<string, string>;
  aoIniciar: (sessao: string, nome: string, contato: boolean) => void;
}) => {
  const turnstile = useTurnstile(true);
  const [valores, setValores] = useState<Record<Campo, string>>({
    nome: "",
    whatsapp: "",
    email: "",
  });
  const [site, setSite] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [erros, setErros] = useState<Partial<Record<Campo, string>>>({});
  const [aviso, setAviso] = useState<string | null>(null);

  const mudar = (c: Campo) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setValores((v) => ({ ...v, [c]: e.target.value }));
    setErros((x) => ({ ...x, [c]: undefined }));
  };

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (enviando) return;
    const faltam: Partial<Record<Campo, string>> = {};
    if (valores.nome.trim().length < 2) faltam.nome = "Informe o nome.";
    if (valores.whatsapp.replace(/\D/g, "").length < 10)
      faltam.whatsapp = "Informe um celular com DDD.";
    if (faltam.nome || faltam.whatsapp) {
      setErros(faltam);
      return;
    }
    if (!turnstile.token) {
      setAviso(
        turnstile.falhou
          ? "Verificação de segurança indisponível. Recarregue a página."
          : "Verificação de segurança em andamento. Tente novamente em instantes.",
      );
      return;
    }
    setEnviando(true);
    setAviso(null);
    const r = await chamarSimulador({
      acao: "iniciar",
      ...valores,
      site,
      turnstile: turnstile.token,
      origem,
    });
    setEnviando(false);
    if (r.sessao) {
      aoIniciar(r.sessao, valores.nome, !!r.contato);
      return;
    }
    // Campo inválido não gasta o token do Turnstile (o servidor valida antes); o resto gasta.
    if (r.campo === "nome" || r.campo === "whatsapp" || r.campo === "email") {
      setErros({ [r.campo]: r.erro ?? "Campo inválido." });
      return;
    }
    turnstile.renovar();
    setAviso(
      r.erro ??
        "Não foi possível iniciar a simulação. Tente novamente em instantes.",
    );
  };

  // Sobre o fundo da marca (Rick, 18/10/2026: o formulário acompanha a identidade da página).
  const campo = (temErro: boolean) =>
    `w-full rounded-xl border bg-white/[0.06] px-4 py-3 text-base text-white placeholder:text-white/35 focus:outline-none focus:ring-2 focus:ring-[#c4ae90]/60 sm:text-sm ${
      temErro ? "border-[#f87171]" : "border-white/15 focus:border-[#c4ae90]/70"
    }`;
  const rotulo =
    "mb-1.5 block text-[11px] font-semibold uppercase tracking-[2px] text-[#A8AEC0]";
  const erroCampo = "mt-1.5 text-xs text-[#fca5a5]";

  return (
    <form
      onSubmit={enviar}
      noValidate
      className="w-full space-y-4 rounded-2xl border border-white/10 bg-white/[0.04] p-5 shadow-2xl backdrop-blur-sm sm:p-7"
    >
      <div>
        <label htmlFor="sim-nome" className={rotulo}>
          Nome
        </label>
        <input
          id="sim-nome"
          className={campo(!!erros.nome)}
          value={valores.nome}
          onChange={mudar("nome")}
          maxLength={120}
          autoComplete="name"
          aria-invalid={!!erros.nome}
        />
        {erros.nome && <p className={erroCampo}>{erros.nome}</p>}
      </div>
      <div>
        <label htmlFor="sim-whatsapp" className={rotulo}>
          WhatsApp
        </label>
        <input
          id="sim-whatsapp"
          className={campo(!!erros.whatsapp)}
          value={valores.whatsapp}
          onChange={mudar("whatsapp")}
          inputMode="tel"
          autoComplete="tel"
          placeholder="(41) 99999-9999"
          maxLength={20}
          aria-invalid={!!erros.whatsapp}
        />
        {erros.whatsapp && <p className={erroCampo}>{erros.whatsapp}</p>}
      </div>
      <div>
        <label htmlFor="sim-email" className={rotulo}>
          E-mail (opcional)
        </label>
        <input
          id="sim-email"
          type="email"
          className={campo(!!erros.email)}
          value={valores.email}
          onChange={mudar("email")}
          autoComplete="email"
          maxLength={160}
          aria-invalid={!!erros.email}
        />
        {erros.email && <p className={erroCampo}>{erros.email}</p>}
      </div>
      {/* Honeypot: invisível para pessoas; robô preenche. */}
      <input
        type="text"
        name="site"
        value={site}
        onChange={(e) => setSite(e.target.value)}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="hidden"
      />
      <div ref={turnstile.ref} />
      {aviso && <p className="text-xs text-[#fcd34d]">{aviso}</p>}
      <button
        type="submit"
        disabled={enviando}
        className="w-full rounded-xl px-4 py-3.5 text-sm font-bold uppercase tracking-[2px] transition hover:brightness-105 disabled:opacity-60"
        style={{ backgroundColor: DOURADO, color: AZUL_ESCURO }}
      >
        {enviando ? "Iniciando…" : "Iniciar simulação"}
      </button>
      <p className="text-[11px] leading-snug text-white/40">
        {AVISO_LEGAL} Ao enviar os dados, você autoriza a North a falar com você
        sobre esta simulação.
      </p>
    </form>
  );
};

export const SimuladorPage = () => {
  const [searchParams] = useSearchParams();
  const origem = useMemo(
    () => ({
      ...origemDaVisita(
        searchParams,
        new URLSearchParams(window.location.search),
        document.referrer,
      ),
      pagina: "/simule",
    }),
    // Só na chegada.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const [estado, setEstado] = useState<Estado>(estadoInicial);
  const [texto, setTexto] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  const fimRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    try {
      if (estado.sessao)
        sessionStorage.setItem(CHAVE_SESSAO, JSON.stringify(estado));
    } catch {
      // sem sessionStorage: a conversa só não sobrevive a um recarregar.
    }
  }, [estado]);

  useEffect(() => {
    fimRef.current?.scrollIntoView?.({ behavior: "smooth", block: "end" });
  }, [estado.mensagens.length, enviando]);

  const recomecar = (msg: string) => {
    try {
      sessionStorage.removeItem(CHAVE_SESSAO);
    } catch {
      // nada a limpar
    }
    setEstado(estadoVazio());
    setAviso(msg);
  };

  const enviar = async (conteudo: string) => {
    const t = conteudo.trim().slice(0, LIMITE_CARACTERES);
    if (!t || enviando || estado.encerrada || !estado.sessao) return;
    setAviso(null);
    setTexto("");
    setEnviando(true);
    setEstado((e) => ({
      ...e,
      mensagens: [...e.mensagens, { papel: "cliente", texto: t }],
    }));

    const r = await chamarSimulador({
      acao: "mensagem",
      texto: t,
      sessao: estado.sessao,
    });
    setEnviando(false);

    if (r.expirada) {
      recomecar(r.erro ?? "Conversa expirada.");
      return;
    }
    if (r.erro) {
      setAviso(r.erro);
      // A pergunta não foi respondida: sai da conversa e volta para a caixa de texto.
      setEstado((e) => ({
        ...e,
        mensagens: e.mensagens.slice(0, -1),
        encerrada: e.encerrada || !!r.encerrada,
      }));
      setTexto(t);
      return;
    }
    setEstado((e) => ({
      ...e,
      mensagens: [...e.mensagens, { papel: "ia", texto: r.resposta ?? "" }],
      contato: e.contato || !!r.contato,
      encerrada: r.restantes === 0,
    }));
  };

  const conversaIniciada = estado.mensagens.some((m) => m.papel === "cliente");

  // Antes da sessão: página inteira na identidade da marca, sem divisão de tela.
  if (!estado.sessao) {
    return (
      <div
        className="flex min-h-screen flex-col"
        style={{
          background: `radial-gradient(120% 80% at 50% 0%, ${ACCENT} 0%, ${AZUL_ESCURO} 70%)`,
        }}
      >
        <header className="shrink-0 px-6 pt-5 xl:px-14">
          <a href="https://northbusinessteam.com.br/" className="inline-block">
            <img
              src="/logos/logo_full.png"
              alt="North Business Team"
              className="h-8 w-auto lg:h-9"
            />
          </a>
        </header>
        <main className="mx-auto flex w-full max-w-xl flex-1 flex-col justify-center px-4 py-8 sm:px-6">
          <p
            className="mb-3 text-[11px] font-bold uppercase tracking-[6px]"
            style={{ color: DOURADO }}
          >
            — Simulador de consórcio
          </p>
          <h1 className="mb-3 text-3xl font-extrabold leading-tight tracking-tight text-white lg:text-5xl">
            Imóvel ou veículo, <span style={{ color: DOURADO }}>sem juros</span>
          </h1>
          <p className="mb-7 max-w-md leading-relaxed text-[#A8AEC0]">
            Simulação com os grupos disponíveis, parcela reduzida até a
            contemplação e atendimento de um consultor da North.
          </p>
          {aviso && <p className="mb-3 text-xs text-[#fcd34d]">{aviso}</p>}
          <FormularioInicio
            origem={origem}
            aoIniciar={(sessao, nome, contato) => {
              setAviso(null);
              setEstado({
                sessao,
                mensagens: [saudacao(nome)],
                contato,
                encerrada: false,
              });
            }}
          />
        </main>
        <footer className="shrink-0 px-6 py-4 xl:px-14">
          <p className="text-xs text-[#A8AEC0]/70">
            © {new Date().getFullYear()} North Business Team. Todos os direitos
            reservados.
          </p>
        </footer>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col lg:h-screen lg:flex-row lg:overflow-hidden">
      <div
        className="flex flex-col lg:w-5/12"
        style={{ backgroundColor: ACCENT }}
      >
        <header className="shrink-0 px-6 pt-5 xl:px-14">
          <a href="https://northbusinessteam.com.br/" className="inline-block">
            <img
              src="/logos/logo_full.png"
              alt="North Business Team"
              className="h-8 w-auto lg:h-9"
            />
          </a>
        </header>
        <section className="flex flex-col justify-center px-6 py-6 lg:flex-1 lg:py-12 xl:px-14">
          <p
            className="mb-3 text-[11px] font-bold uppercase tracking-[6px]"
            style={{ color: DOURADO }}
          >
            — Simulador de consórcio
          </p>
          <h1 className="mb-3 text-2xl font-extrabold leading-tight tracking-tight text-white lg:text-4xl">
            Imóvel ou veículo, <span style={{ color: DOURADO }}>sem juros</span>
          </h1>
          <p className="hidden max-w-md leading-relaxed text-[#A8AEC0] lg:block">
            Simulação com os grupos disponíveis, parcela reduzida até a
            contemplação e atendimento de um consultor da North.
          </p>
        </section>
        <footer
          className="hidden shrink-0 px-6 py-4 lg:block xl:px-14"
          style={{ backgroundColor: AZUL_ESCURO }}
        >
          <p className="text-xs text-[#A8AEC0]">
            © {new Date().getFullYear()} North Business Team. Todos os direitos
            reservados.
          </p>
        </footer>
      </div>

      <main className="flex flex-1 flex-col bg-white lg:w-7/12">
        <div className="border-b border-[#e5e7eb] px-4 py-3">
          <p className="text-sm font-semibold" style={{ color: AZUL_ESCURO }}>
            Assistente North
          </p>
          <p className="text-xs text-[#6b7280]">Atendimento virtual</p>
        </div>

        <>
          <div
            className="flex-1 space-y-3 overflow-y-auto px-4 py-4"
            aria-live="polite"
          >
            {estado.mensagens.map((m, i) => (
              <div
                key={i}
                className={
                  m.papel === "cliente"
                    ? "flex justify-end"
                    : "flex justify-start"
                }
              >
                <div
                  className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    m.papel === "cliente"
                      ? "text-white"
                      : "bg-[#f3f4f6] text-[#111827]"
                  }`}
                  style={
                    m.papel === "cliente"
                      ? { backgroundColor: ACCENT }
                      : undefined
                  }
                >
                  <Texto texto={m.texto} />
                </div>
              </div>
            ))}
            {enviando && (
              <div className="flex justify-start">
                <div className="rounded-2xl bg-[#f3f4f6] px-4 py-2.5 text-sm text-[#6b7280]">
                  Digitando…
                </div>
              </div>
            )}
            {!conversaIniciada && !enviando && (
              <div className="flex flex-wrap gap-2 pt-1">
                {SUGESTOES.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => enviar(s)}
                    className="rounded-full border px-3 py-1.5 text-xs font-medium"
                    style={{ borderColor: ACCENT, color: ACCENT }}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
            {estado.encerrada && (
              <p className="rounded-lg bg-[#ecfdf5] px-3 py-2 text-xs text-[#065f46]">
                Conversa encerrada. Um consultor da North entrará em contato
                pelo WhatsApp informado.
              </p>
            )}
            <div ref={fimRef} />
          </div>

          <div className="border-t border-[#e5e7eb] px-4 py-3">
            {aviso && <p className="mb-2 text-xs text-[#b45309]">{aviso}</p>}
            <form
              className="flex items-end gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                enviar(texto);
              }}
            >
              <textarea
                value={texto}
                onChange={(e) =>
                  setTexto(e.target.value.slice(0, LIMITE_CARACTERES))
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    enviar(texto);
                  }
                }}
                rows={1}
                maxLength={LIMITE_CARACTERES}
                disabled={estado.encerrada}
                placeholder={
                  estado.encerrada
                    ? "Conversa encerrada"
                    : "Digite sua mensagem"
                }
                aria-label="Mensagem"
                className="max-h-32 min-h-[42px] flex-1 resize-none rounded-md border border-[#d1d5db] px-3 py-2 text-base focus:outline-none focus:ring-2 disabled:bg-[#f3f4f6] sm:text-sm"
              />
              <button
                type="submit"
                disabled={enviando || estado.encerrada || !texto.trim()}
                className="h-[42px] rounded-md px-4 text-sm font-semibold text-white disabled:opacity-50"
                style={{ backgroundColor: ACCENT }}
              >
                Enviar
              </button>
            </form>
            <p className="mt-2 text-[11px] leading-snug text-[#9ca3af]">
              {AVISO_LEGAL}
            </p>
          </div>
        </>
      </main>
    </div>
  );
};

SimuladorPage.path = "/simule";
